/**
 * Layout breakpoints for adaptive, device-agnostic design.
 * Based on width (not device type). Responds to orientation, resize, split-screen.
 *
 * Material/Android Window Size Classes alignment:
 * - Compact: phones portrait, small screens
 * - Medium: tablets portrait, large phones landscape
 * - Expanded: tablets landscape
 * - Large: large tablets, desktops
 * - Extra-large: desktop-scale
 */

export const LAYOUT_BREAKPOINTS = {
  /** < 600: phones portrait, compact */
  compactMax: 599,
  /** 600–839: tablets portrait, large phones landscape */
  mediumMin: 600,
  mediumMax: 839,
  /** 840–1199: tablets landscape */
  expandedMin: 840,
  expandedMax: 1199,
  /** 1200–1599: large tablets, small desktops */
  largeMin: 1200,
  largeMax: 1599,
  /** ≥ 1600: desktop-scale */
  extraLargeMin: 1600,
} as const;

export type LayoutSizeClass =
  | 'compact'
  | 'medium'
  | 'expanded'
  | 'large'
  | 'extraLarge';

export function getSizeClass(width: number): LayoutSizeClass {
  if (width >= LAYOUT_BREAKPOINTS.extraLargeMin) return 'extraLarge';
  if (width >= LAYOUT_BREAKPOINTS.largeMin) return 'large';
  if (width >= LAYOUT_BREAKPOINTS.expandedMin) return 'expanded';
  if (width >= LAYOUT_BREAKPOINTS.mediumMin) return 'medium';
  return 'compact';
}

/** True when width >= 600 (medium or larger) */
export function isMediumOrLarger(width: number): boolean {
  return width >= LAYOUT_BREAKPOINTS.mediumMin;
}

/** True when width >= 840 (expanded or larger - tablets landscape) */
export function isExpandedOrLarger(width: number): boolean {
  return width >= LAYOUT_BREAKPOINTS.expandedMin;
}
